import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Grid, Typography, Box, IconButton } from '@mui/material';

import {
  FHAntdDateTimePicker,
  FHAutocomplete,
  FHButton,
  FHConfirmDialog,
} from '../../components/base';
import { FORTA_BLUE, FORTA_ERROR, FORTA_SUCCESS, FORTA_WHITE } from '../../utils/colors';
import { getProviderAssignmentStatus } from '../../utils/common';
import { areUserRolesIn, disableNonFriday, disableNonSaturday } from '../../helpers';
import { ClinicalRelationships, Roles, Themes, clinicalRelationshipOptions } from '../../constants';
import EditOutlinedIcon from '../../components/svgs/EditOutlinedIcon';
import TrashIcon from '../../components/svgs/TrashIcon3';

import type { RootState } from '../../store';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type { IOption, IProvider, IProviderAssignment } from '../../utils/types';
interface IProviderAssignmentRowProps {
  providerAssignment: IProviderAssignment;
  isAdding: boolean;
  isEditing: boolean;
  isDisabled: boolean;
  onCancel: () => void;
  onEdit: () => void;
  onSave: (editableAssignment: IProviderAssignment) => void;
  onDelete: (editableAssignment: IProviderAssignment) => void;
}
const ProviderAssignmentRow = ({
  providerAssignment,
  isAdding,
  isDisabled,
  isEditing,
  onSave,
  onCancel,
  onEdit,
  onDelete,
}: IProviderAssignmentRowProps) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { allProvidersList } = useSelector((state: RootState) => state.providers);

  const [selectedProvider, setSelectedProvider] = useState<IProvider | null>();
  const [selectedClinicalRelationship, setSelectedClinicalRelationship] = useState<string | null>(
    null,
  );
  const [editableAssignment, setEditableAssignment] = useState({ ...providerAssignment });
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const isAdminUser = areUserRolesIn(user?.roles, [Roles.Admin]);

  useEffect(() => {
    setEditableAssignment({ ...providerAssignment });
    setSelectedProvider(providerAssignment?.provider?.id ? providerAssignment.provider : null);
    setSelectedClinicalRelationship(
      providerAssignment?.clinical_relationship
        ? clinicalRelationshipOptions.find(
            (option) => option.value === providerAssignment?.clinical_relationship,
          )?.label ?? null
        : null,
    );
  }, [providerAssignment]);

  const handleFieldChange = (field: string, value: any) => {
    setEditableAssignment((prevAssignment: IProviderAssignment) => ({
      ...prevAssignment,
      [field]: value,
    }));
  };

  const resetDates = () => {
    setEditableAssignment((prevAssignment: IProviderAssignment) => ({
      ...prevAssignment,
      date_start: '',
      date_end: null,
    }));
  };

  const validateRow = () => {
    if (!selectedProvider) {
      toast.error('Please select a provider');
      return false;
    }
    if (!selectedClinicalRelationship) {
      toast.error('Please select a clinical relationship');
      return false;
    }
    if (!editableAssignment.date_start) {
      toast.error('Please select date assigned');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (isAdding) {
      if (!validateRow()) return;
      onSave(editableAssignment);
      return;
    }

    if (isEditing) {
      if (!editableAssignment.date_start) {
        toast.error('Please select date assigned');
        return;
      }
      onSave(editableAssignment);
    }
  };

  const handleCancel = () => {
    setSelectedProvider(null);
    setSelectedClinicalRelationship(null);
    setEditableAssignment({ ...providerAssignment });
    onCancel();
  };

  const handleDisableStartDate = (current: Moment) => {
    if (!current) return false;
    // Disable if date is after end date.
    if (editableAssignment.date_end && current.isAfter(editableAssignment.date_end, 'day')) {
      return true;
    }
    return disableNonSaturday(current);
  };

  const handleDisableEndDate = (current: Moment) => {
    if (!current) return false;
    // Disable if date is before start date.
    if (editableAssignment.date_start && current.isBefore(editableAssignment.date_start, 'day')) {
      return true;
    }
    return disableNonFriday(current);
  };

  return (
    <>
      <Grid
        container
        p={2}
        spacing={1}
        direction="row"
        alignItems="center"
        sx={{ backgroundColor: FORTA_WHITE, borderRadius: '10px', mb: 2 }}
      >
        <Grid item xs={2}>
          {isAdding ? (
            <FHAutocomplete
              disableClearable
              inputHeight={40}
              sx={{ width: '100%', boxShadow: 'none' }}
              placeholder="Select provider"
              coloredBackground
              options={allProvidersList.map((provider: IProvider) => ({
                id: provider.id,
                label:
                  provider?.roles && provider?.roles?.length > 0
                    ? `${provider.first_name} ${provider.last_name} (${provider.roles.join(', ')})`
                    : `${provider.first_name} ${provider.last_name}`,
              }))}
              renderOption={(props: any, option: IOption) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
              value={selectedProvider ? selectedProvider : null}
              onChange={(event: ChangeEvent<HTMLSelectElement>, newValue: IProvider | null) => {
                handleFieldChange('provider', newValue ? newValue : '');
                setSelectedProvider(newValue);
              }}
            />
          ) : (
            <Typography textAlign={'left'}>
              <React.Fragment key={providerAssignment?.provider.id}>
                <Link to={'/admin/provider/' + providerAssignment?.provider?.id}>
                  {`${providerAssignment?.provider?.first_name} ${providerAssignment?.provider?.last_name}`}
                </Link>
              </React.Fragment>
            </Typography>
          )}
        </Grid>
        <Grid item xs={2}>
          {isAdding ? (
            <FHAutocomplete
              inputHeight={40}
              disableClearable
              sx={{ width: '100%', boxShadow: 'none' }}
              coloredBackground
              placeholder="Select clinical relationship"
              options={clinicalRelationshipOptions}
              value={selectedClinicalRelationship || null}
              onChange={(
                event: ChangeEvent<HTMLSelectElement>,
                newValue: (typeof clinicalRelationshipOptions)[number] | null,
              ) => {
                handleFieldChange('clinical_relationship', newValue ? newValue.value : '');
                resetDates();
                setSelectedClinicalRelationship(newValue?.label ?? null);
              }}
            />
          ) : (
            <Typography textAlign={'center'}>
              {
                clinicalRelationshipOptions.find(
                  (option) => option.value === providerAssignment?.clinical_relationship,
                )?.label
              }
            </Typography>
          )}
        </Grid>
        <Grid item xs={2}>
          {isAdding || isEditing ? (
            <FHAntdDateTimePicker
              format="MM/DD/YYYY"
              inputHeight={40}
              style={{ width: '100%', boxShadow: 'none' }}
              allowClear={false}
              coloredBackground
              inputReadOnly={true}
              disabledDate={
                selectedClinicalRelationship &&
                editableAssignment.clinical_relationship === ClinicalRelationships.BobbingBcba
                  ? handleDisableStartDate
                  : undefined
              }
              value={
                editableAssignment.date_start
                  ? moment(editableAssignment.date_start, 'YYYY-MM-DD')
                  : ''
              }
              onSelect={(value: Moment) =>
                handleFieldChange('date_start', value.format('YYYY-MM-DD'))
              }
              onChange={(value: Moment | null) =>
                handleFieldChange('date_start', value ? value.format('YYYY-MM-DD') : null)
              }
            />
          ) : (
            <Typography textAlign={'center'}>
              {providerAssignment?.date_start
                ? moment(providerAssignment?.date_start).format('MM/DD/YYYY')
                : ''}
            </Typography>
          )}
        </Grid>

        <Grid item xs={2}>
          {isAdding || isEditing ? (
            <FHAntdDateTimePicker
              format="MM/DD/YYYY"
              inputHeight={40}
              style={{
                width: '100%',
                boxShadow: 'none',
              }}
              coloredBackground
              inputReadOnly={true}
              allowClear={false}
              disabledDate={
                selectedClinicalRelationship &&
                editableAssignment.clinical_relationship === ClinicalRelationships.BobbingBcba
                  ? handleDisableEndDate
                  : undefined
              }
              value={
                editableAssignment.date_end
                  ? moment(editableAssignment.date_end, 'YYYY-MM-DD')
                  : null
              }
              onSelect={(value: Moment) =>
                handleFieldChange('date_end', value.format('YYYY-MM-DD'))
              }
              onChange={(value: Moment | null) =>
                handleFieldChange('date_end', value ? value.format('YYYY-MM-DD') : null)
              }
            />
          ) : (
            <Typography textAlign={'center'}>
              {providerAssignment?.date_end
                ? moment(providerAssignment?.date_end).format('MM/DD/YYYY')
                : 'N/A'}
            </Typography>
          )}
        </Grid>
        <Grid item xs={2}>
          <Typography
            textAlign={'center'}
            color={
              getProviderAssignmentStatus(providerAssignment) === 'Assigned'
                ? FORTA_SUCCESS
                : FORTA_ERROR
            }
          >
            {getProviderAssignmentStatus(providerAssignment)}
          </Typography>
        </Grid>
        {isAdding || isEditing ? (
          <>
            <Grid item xs={1} justifyContent={'center'}>
              <FHButton disabled={isDisabled} onClick={handleSave}>
                {isAdding ? 'Save' : 'Update'}
              </FHButton>
            </Grid>
            <Grid item xs={1}>
              <FHButton
                sx={{
                  border: `2px solid ${FORTA_BLUE.primary}`,
                }}
                lightBackground={true}
                onClick={handleCancel}
              >
                Cancel
              </FHButton>
            </Grid>
          </>
        ) : (
          <>
            {areUserRolesIn(user?.roles, [Roles.Admin, Roles.OnboardingCoordinator]) && (
              <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton
                  sx={{
                    width: 54,
                    height: 30,
                    border: `2px solid ${FORTA_BLUE.primary}`,
                    borderRadius: '20px',
                    backgroundColor: isAdding ? FORTA_BLUE.primary : 'transparent',
                    mr: 3,
                    gap: 1,
                  }}
                  disabled={isDisabled}
                  onClick={onEdit}
                >
                  <EditOutlinedIcon width={16} color={FORTA_BLUE.primary} />
                </IconButton>
              </Grid>
            )}
            {isAdminUser && (
              <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton
                  sx={{
                    width: 54,
                    height: 30,
                    border: `2px solid ${FORTA_BLUE.primary}`,
                    borderRadius: '20px',
                    backgroundColor: isAdding ? FORTA_BLUE.primary : 'transparent',
                    mr: 3,
                    gap: 1,
                  }}
                  disabled={isDisabled}
                  onClick={() => setShowConfirmDialog(true)}
                >
                  <TrashIcon width={16} color={FORTA_BLUE.primary} />
                </IconButton>
              </Grid>
            )}
          </>
        )}
      </Grid>
      <FHConfirmDialog
        open={showConfirmDialog}
        colorTheme={Themes.Primary}
        title={'Delete provider assignment'}
        contentRenderer={() => (
          <Box sx={{ textAlign: 'center', fontSize: 21, padding: '48px 0px 48px 0px' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 21, mb: 2 }}>
              Are you sure you want to delete this provider assignment?
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 21 }}>
              This action cannot be undone.
            </Typography>
          </Box>
        )}
        submittext={'Delete'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmDialog(false)}
        onSubmit={() => {
          setShowConfirmDialog(false);
          onDelete(editableAssignment);
        }}
      />
    </>
  );
};

export default ProviderAssignmentRow;
